import { useNavigate } from "react-router-dom";
import logo from "../buckland.png";
import React, { useContext, useEffect, useState } from "react";
import Select from "react-select";
import { DataContext } from "../contexts/DataContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faTimes,
  faCog,
  faBuilding,
  faMapMarkerAlt,
  faFileAlt,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import ReferencesPerEntryPointBarChart from "./ReferencesPerEntryPointBarChart";
import ReferencesPerDocumentTypeBarChart from "./ReferencesPerDocumentTypeBarChart";
import ReferencesPerStatusBarChart from "./ReferencesPerStatusBarChart";
import ReferencesPerOperationTypeDonutChart from "./ReferencesPerOperationTypeDonutChart";
import ReferencesPerEntryPointAndLicenseBarChart from "./ReferencesPerEntryPointAndLicenseBarChart";
import ReferencesDistributionByCarrierBarChart from "./ReferencesDistributionByCarrierBarChart";
import TrafficDashboardDataGrid from "./TrafficDashboardDataGrid";
import TrafficMiniDashboard from "./TrafficMiniDashboard";
import AttentionTimeBarChart from "./AttentionTimeBarChart";
import DocTimeBarChart from "./DocTimeBarChart";
import AttentionDocumentTimesSeriesChart from "./AttentionDocumentTimesSeriesChart";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { faCalendar, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";

function TrafficDashboard() {
  const navigate = useNavigate();

  const { trafficData, isLoading, fetchTrafficData } = useContext(DataContext);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    IE: [],
    Aduana: [],
    Regimen: [],
    FacturistaNombre: [],
    ClienteNombre: [],
    CustomerDivision: [],
  });
  const [filterVisible, setFilterVisible] = useState(true); // Default to visible for dashboard
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    fetchTrafficData();
  }, [fetchTrafficData]);

  useEffect(() => {
    setFilteredData(trafficData);
  }, [trafficData]);

  useEffect(() => {
    if (trafficData) {
      let data = trafficData.filter(
        (item) =>
          (filters.IE.length > 0
            ? filters.IE.includes(item.header.operation_type)
            : true) &&
          (filters.Aduana.length > 0
            ? filters.Aduana.includes(
                item.header.pedimento.pedimento_entry_point
              )
            : true) &&
          (filters.Regimen.length > 0
            ? filters.Regimen.includes(item.header.detail?.traffic_data?.regime)
            : true) &&
          (filters.FacturistaNombre.length > 0
            ? filters.FacturistaNombre.includes(item.header.customer_name)
            : true) &&
          (filters.ClienteNombre.length > 0
            ? filters.ClienteNombre.includes(item.header.customer_name)
            : true) &&
          (filters.CustomerDivision.length > 0
            ? filters.CustomerDivision.includes(item.header.customer_division)
            : true)
      );

      // Apply date range filter
      const { startDate, endDate } = dateRange[0];
      if (startDate && endDate) {
        data = data.filter((item) => {
          const dateInit = item.header.detail?.dates_data?.date_init
            ? new Date(item.header.detail.dates_data.date_init)
            : null;
          return dateInit && dateInit >= startDate && dateInit <= endDate;
        });
      }

      setFilteredData(data);
    }
  }, [filters, trafficData, dateRange]);

  const createFilterOptions = (field) => {
    if (!trafficData) return [];

    const uniqueValues = new Set();
    trafficData.forEach((item) => {
      const value = field
        .split(".")
        .reduce(
          (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
          item
        );
      if (value) uniqueValues.add(value);
    });

    return Array.from(uniqueValues).map((value) => {
      return { value, label: value };
    });
  };

  const clearDateRangeFilter = () => {
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
  };

  const toggleFilters = () => {
    setFilterVisible(!filterVisible);
  };

  const clearFilter = (filterKey) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: [],
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      IE: [],
      Aduana: [],
      Regimen: [],
      FacturistaNombre: [],
      ClienteNombre: [],
      CustomerDivision: [],
    });
    clearDateRangeFilter();
  };

  const formatDate = (dateString) => {
    const options = { month: "long", day: "numeric", year: "numeric" };
    const date = new Date(dateString);
    return isNaN(date)
      ? "Date N/A"
      : date.toLocaleDateString(undefined, options);
  };

  // Create a new component for the enhanced filter section
  const renderFilterSection = () => (
    <div className="w-full bg-white rounded-lg shadow-sm p-5 mb-10 border border-gray-300">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          <FontAwesomeIcon icon={faFilter} className="mr-2 text-blue-600" />
          Dashboard Filters
        </h2>
        <div className="flex items-center gap-3">
          {dateRange[0].startDate && dateRange[0].endDate && (
            <div className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-md flex items-center border border-blue-200">
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
              {formatDate(dateRange[0].startDate)} -{" "}
              {formatDate(dateRange[0].endDate)}
              <button
                onClick={clearDateRangeFilter}
                className="ml-2 text-blue-500 hover:text-blue-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}

          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md flex items-center shadow-sm transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faCalendar} className="mr-2" />
            {showDatePicker ? "Hide Date Range" : "Select Date Range"}
          </button>

          <FontAwesomeIcon
            icon={filterVisible ? faChevronDown : faChevronRight}
            onClick={toggleFilters}
            className="cursor-pointer text-gray-700 hover:text-blue-600"
          />
        </div>
      </div>

      {showDatePicker && (
        <div className="relative">
          <div className="absolute right-0 z-50 mt-1">
            <div className="border border-gray-300 rounded-lg shadow-md bg-white">
              <DateRangePicker
                ranges={dateRange}
                onChange={(ranges) => setDateRange([ranges.selection])}
              />
            </div>
          </div>
        </div>
      )}

      {filterVisible && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faCog} className="mr-1 text-blue-600" />
                Operation Type
              </label>
              <Select
                isMulti
                options={createFilterOptions("header.operation_type")}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                placeholder="Filter by Op. Type"
                value={createFilterOptions("header.operation_type").filter(
                  (option) => filters.IE.includes(option.value)
                )}
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    IE: selectedOptions.map((option) => option.value),
                  }))
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-1 text-blue-600"
                />
                Customer
              </label>
              <Select
                isMulti
                options={createFilterOptions("header.customer_name")}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                placeholder="Filter by Customer"
                value={createFilterOptions("header.customer_name").filter(
                  (option) => filters.ClienteNombre.includes(option.value)
                )}
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    ClienteNombre: selectedOptions.map(
                      (option) => option.value
                    ),
                  }))
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>
            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-1 text-blue-600"
                />
                Division
              </label>
              <Select
                isMulti
                options={createFilterOptions("header.customer_division")}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                placeholder="Filter by Division"
                value={createFilterOptions("header.customer_division").filter(
                  (option) => filters.CustomerDivision.includes(option.value)
                )}
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    CustomerDivision: selectedOptions.map(
                      (option) => option.value
                    ),
                  }))
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>
            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="mr-1 text-blue-600"
                />
                Entry Point
              </label>
              <Select
                isMulti
                options={createFilterOptions(
                  "header.pedimento.pedimento_entry_point"
                )}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                placeholder="Filter by Entry Point"
                value={createFilterOptions(
                  "header.pedimento.pedimento_entry_point"
                ).filter((option) => filters.Aduana.includes(option.value))}
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    Aduana: selectedOptions.map((option) => option.value),
                  }))
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon
                  icon={faFileAlt}
                  className="mr-1 text-blue-600"
                />
                Regimen
              </label>
              <Select
                isMulti
                options={createFilterOptions(
                  "header.detail.traffic_data.regime"
                )}
                className="basic-multi-select"
                classNamePrefix="select-filter"
                placeholder="Filter by Regimen"
                value={createFilterOptions(
                  "header.detail.traffic_data.regime"
                ).filter((option) => filters.Regimen.includes(option.value))}
                onChange={(selectedOptions) =>
                  setFilters((prev) => ({
                    ...prev,
                    Regimen: selectedOptions.map((option) => option.value),
                  }))
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#e1effe",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1e40af",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#60a5fa",
                    ":hover": {
                      backgroundColor: "#bfdbfe",
                      color: "#1e3a8a",
                    },
                  }),
                }}
              />
            </div>
          </div>

          {/* Active Filters Display */}
          {(Object.entries(filters).some(([_, value]) => value.length > 0) ||
            (dateRange[0].startDate && dateRange[0].endDate)) && (
            <div className="mt-4 text-sm text-gray-600">
              <div className="flex items-center flex-wrap">
                <span className="mr-2">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {filters.IE.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {filters.IE.map((value) => (
                        <span
                          key={`IE-${value}`}
                          className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                        >
                          Op. Type: {value}
                          <button
                            onClick={() => {
                              setFilters((prev) => ({
                                ...prev,
                                IE: prev.IE.filter((v) => v !== value),
                              }));
                            }}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-xs"
                            />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {filters.ClienteNombre.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {filters.ClienteNombre.map((value) => (
                        <span
                          key={`Cliente-${value}`}
                          className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                        >
                          Customer: {value}
                          <button
                            onClick={() => {
                              setFilters((prev) => ({
                                ...prev,
                                ClienteNombre: prev.ClienteNombre.filter(
                                  (v) => v !== value
                                ),
                              }));
                            }}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-xs"
                            />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {filters.CustomerDivision.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {filters.CustomerDivision.map((value) => (
                        <span
                          key={`Division-${value}`}
                          className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                        >
                          Division: {value}
                          <button
                            onClick={() => {
                              setFilters((prev) => ({
                                ...prev,
                                CustomerDivision: prev.CustomerDivision.filter(
                                  (v) => v !== value
                                ),
                              }));
                            }}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-xs"
                            />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {filters.Aduana.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {filters.Aduana.map((value) => (
                        <span
                          key={`Aduana-${value}`}
                          className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                        >
                          Entry Point: {value}
                          <button
                            onClick={() => {
                              setFilters((prev) => ({
                                ...prev,
                                Aduana: prev.Aduana.filter((v) => v !== value),
                              }));
                            }}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-xs"
                            />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {filters.Regimen.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {filters.Regimen.map((value) => (
                        <span
                          key={`Regimen-${value}`}
                          className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                        >
                          Regimen: {value}
                          <button
                            onClick={() => {
                              setFilters((prev) => ({
                                ...prev,
                                Regimen: prev.Regimen.filter(
                                  (v) => v !== value
                                ),
                              }));
                            }}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-xs"
                            />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={clearAllFilters}
                    className="text-red-600 hover:text-red-800 underline text-xs ml-2"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <nav
        className="flex justify-between items-center px-6 py-4 bg-white shadow"
        style={{ backgroundColor: "#1A2654" }}
      >
        <div className="flex items-center">
          <img src={logo} alt="Wisdom logo" className="h-16 mr-2" />
        </div>
        <div>
          <span className="text-xl font-semibold text-white">Control Deck</span>
        </div>
      </nav>

      <div className="flex-grow overflow-auto">
        <header className="px-6 py-8">
          <h1 className="text-3xl font-bold text-gray-700 mb-4">Overview</h1>

          {/* Filter section with increased margin */}
          {renderFilterSection()}

          {/* Mini Dashboard with top margin */}
          <div className="mt-8 mb-8">
            <TrafficMiniDashboard trafficData={filteredData || []} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            <div>
              <ReferencesPerEntryPointBarChart
                data={filteredData}
                isLoading={isLoading}
              />
            </div>
            <div>
              <ReferencesPerDocumentTypeBarChart
                data={filteredData}
                isLoading={isLoading}
              />
            </div>
            <div>
              <ReferencesPerStatusBarChart
                data={filteredData}
                isLoading={isLoading}
              />
            </div>
            <div>
              <ReferencesPerOperationTypeDonutChart
                data={filteredData}
                isLoading={isLoading}
              />
            </div>
            <div>
              <ReferencesPerEntryPointAndLicenseBarChart
                data={filteredData}
                isLoading={isLoading}
              />
            </div>

            <div>
              <AttentionTimeBarChart
                data={filteredData}
                isLoading={isLoading}
              />
            </div>
            <div>
              <AttentionDocumentTimesSeriesChart
                data={filteredData}
                isLoading={isLoading}
              />
            </div>
            <div>
              <DocTimeBarChart data={filteredData} isLoading={isLoading} />
            </div>
            <div className="col-span-4">
              <TrafficDashboardDataGrid data={filteredData} />
            </div>
          </div>
        </header>
      </div>
    </div>
  );
}

export default TrafficDashboard;
